import { alpha, useTheme } from "@mui/material/styles";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Amplify } from "aws-amplify";
import {
  Authenticator,
  ThemeProvider as AmplifyThemeProvider,
} from "@aws-amplify/ui-react";
import { useMemo } from "react";
import { ToastContainer } from "react-toastify";

import { AuthProvider } from "../contexts/auth.context";
import { TrpcClientProvider } from "../contexts/trpc-client.context";

import LoginPage from "../pages/login.page";
import LogoutPage from "../pages/logout.page";
import NoInternetPage from "../pages/no-internet.page";
import CardImportPage from "../pages/card-import.page";
import DecksPage from "../pages/decks.page";
import NewDeckPage from "../pages/new-deck.page";
import AdminPage from "../pages/admin.page";
import CanonicalBootstrapAdminPage from "../pages/canonical-bootstrap-admin.page";
import TagAliasBootstrapAdminPage from "../pages/tag-alias-bootstrap-admin.page";

import ErrorBoundary from "./error-boundary.component";
import { RequireAuth } from "./require-auth.component";
import { RequireAdmin } from "./require-admin.component";

import "@aws-amplify/ui-react/styles.css";
import { createAmplifyAuthTheme } from "../theme/amplifyTheme";
import { useThemeMode } from "../theme/theme-mode.context";

if (
  !process.env.REACT_APP_USER_POOL_ID ||
  !process.env.REACT_APP_USER_POOL_CLIENT_ID
) {
  throw new Error("Missing AWS config data");
}

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
    },
  },
});

function App() {
  const theme = useTheme();
  const { isDarkMode } = useThemeMode();
  const amplifyTheme = useMemo(() => createAmplifyAuthTheme(theme), [theme]);

  return (
    <ErrorBoundary>
      <AmplifyThemeProvider
        colorMode={isDarkMode ? "dark" : "light"}
        theme={amplifyTheme}
      >
        <Authenticator.Provider>
          <BrowserRouter>
            <AuthProvider>
              <TrpcClientProvider>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/logout" element={<LogoutPage />} />
                  <Route
                    path="/"
                    element={
                      <RequireAuth>
                        <CardImportPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/decks"
                    element={
                      <RequireAuth>
                        <DecksPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/decks/new"
                    element={
                      <RequireAuth>
                        <NewDeckPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <RequireAdmin>
                        <AdminPage />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/canonical-bootstrap"
                    element={
                      <RequireAdmin>
                        <CanonicalBootstrapAdminPage />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/tag-alias-bootstrap"
                    element={
                      <RequireAdmin>
                        <TagAliasBootstrapAdminPage />
                      </RequireAdmin>
                    }
                  />
                  <Route path="/no-internet" element={<NoInternetPage />} />
                </Routes>
                <ToastContainer
                  position="bottom-right"
                  theme={isDarkMode ? "dark" : "light"}
                  toastStyle={{
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                    boxShadow: theme.shadows[8],
                    borderRadius: `${theme.shape.borderRadius}px`,
                  }}
                />
              </TrpcClientProvider>
            </AuthProvider>
          </BrowserRouter>
        </Authenticator.Provider>
      </AmplifyThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
